import React, { useEffect, useState } from "react";
import { Order } from "../../../interfaces/order";
import { getOrderByID } from "../../../services/orderService";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderForm } from "../../../interfaces/orderForm";
import {
  getOrderFormByID,
  saveMotorVehicleAuthorizationForm,
} from "../../../services/orderFormService";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import {
  getSafePickerDate,
  getStates,
  isValidBirthday,
  isValidDate,
  toSafeDateString,
} from "../../../services/helpers";
import { SimpleClient } from "../../../interfaces/client";
import MonthDayYearPicker from "../../../components/component-tools/MonthDayYearPicker";
import { getSimpleClient } from "../../../services/clientService";

export interface MotorRelease {
  firstName: string;
  middleName: string;
  lastName: string;
  licenseNumber: string;
  licenseState: string;
  dateOfBirth: Date;
}

const MotorVehicleAuthorizationForm = () => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const location = useLocation();
  const { orderID, orderFormID, anyCompletedForms } = location.state || {};
  const [fileUploaded, setFileUploaded] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const states = getStates();
  // const [washingtonStateFormURL, setWashingtonStateFormURL] =
  //   useState<string>("");
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [motorRelease, setMotorRelease] = useState<MotorRelease>({
    firstName: "",
    middleName: "",
    lastName: "",
    licenseNumber: "",
    licenseState: "",
    dateOfBirth: new Date(),
  });

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);
        const simpleClient = await getSimpleClient(order?.clientID);
        setClient(simpleClient);

        // const washingtonStateURL = await getSpecialReleaseFormAdmin(
        //   "Washington State Release Form",
        // );
        // setWashingtonStateFormURL(washingtonStateURL);

        if (anyCompletedForms) {
          setMotorRelease((prev) => ({
            ...prev,
            firstName: order.candidateFirstName ?? "",
            middleName: order.candidateMI ?? "",
            lastName: order.candidateLastName ?? "",
            dateOfBirth: order.candidateDOB ?? new Date(),
          }));
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderFormID, orderID, anyCompletedForms]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (e.target.type === "date" && field === "dateOfBirth") {
      value = new Date(e.target.value);
    }

    if (field in motorRelease) {
      setMotorRelease((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    } else if (field in authorization) {
      setAuthorization((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    }
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Motor Release Validation
    if (!motorRelease?.firstName) {
      errors.firstName = "First name is required.";
    }
    if (!motorRelease?.lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!motorRelease?.licenseNumber) {
      errors.licenseNumber = "Drivers License Number is required.";
    }
    if (!motorRelease?.licenseState) {
      errors.licenseState = "Drivers License State is required.";
    }
    if (!isValidDate(motorRelease?.dateOfBirth)) {
      errors.dateOfBirth = "DOB is required and must be valid.";
    } else if (!isValidBirthday(motorRelease.dateOfBirth)) {
      errors.dateOfBirth = "Please check your birth date";
    }

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    // Release Form Validation
    // if (!fileUploaded && motorRelease.licenseState === "WA") {
    //   errors.specialRelease = "Please upload the release form.";
    // }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // const handleFormUpload = async () => {
  //   if (!orderFormID) {
  //     console.error("Order Form ID is missing");
  //     return;
  //   }

  //   try {
  //     setLoadingSpinner(true);
  //     const fileInput = document.createElement("input");
  //     fileInput.type = "file";
  //     fileInput.accept = ".pdf";
  //     fileInput.onchange = async (event: any) => {
  //       const file = event.target.files[0];
  //       if (!file) return;
  //       setFile(file);
  //       setFileUploaded(true);
  //       setSuccessMessage("Special Release form uploaded successfully");
  //       validationErrors.specialRelease = "";
  //     };
  //     fileInput.click();
  //   } catch (error) {
  //     setFileUploaded(false);
  //     console.error("Error handling file upload:", error);
  //   } finally {
  //     setLoadingSpinner(false);
  //   }
  // };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if ((await validateFields()) && order && orderForm && client) {
        const result = await saveMotorVehicleAuthorizationForm(
          order,
          orderForm?.orderFormID,
          motorRelease,
          authorization,
          client.primaryFirstName ? client.primaryFirstName : "",
          client.primaryLastName ? client.primaryLastName : "",
          client.primaryTitle ? client.primaryTitle : "",
          client.diocese ? client.diocese : "",
          client.clientName,
          file ? file : undefined,
        );

        // if (file && fileUploaded) {
        //   await uploadSpecialReleaseForm(file, orderFormID);
        // }

        if (result) {
          navigate("/candidate/needs-review", {
            state: { successMessage: "Form successfully submitted." },
          });
        }
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      console.error("Error saving motor vehicle authorization:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">
            AUTHORIZATION TO RELEASE MOTOR VEHICLE RECORDS
          </div>

          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I understand that the{" "}
              <span style={{ textDecoration: "underline" }}>
                {client?.diocese}
              </span>{" "}
              also includes a motor vehicle check as part of its background
              investigation on candidates for certain positions. Should a motor
              vehicle check reveal significant problems in this area, the
              information will be given directly and confidentially to{" "}
              <span style={{ textDecoration: "underline" }}>
                {client?.primaryTitle} {client?.primaryFirstName}{" "}
                {client?.primaryLastName}{" "}
              </span>
              or their designated agents.
            </div>

            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I authorize this check to be completed, and hereby release and
              hold harmless from liability any person or organization that
              provides such information.
            </div>

            {/* <div className="text-container" style={{ flex: "0 0 70%" }}>
              {" "}
              <div
                className="text-container"
                style={{ display: "flex", alignItems: "baseline", gap: "20px" }}
              >
                <div>
                  If you hold a Washington State driver’s license, please
                  complete this special release form by printing and uploading
                  it before saving.{" "}
                  <b>Please only upload PDF file types, no images.</b>
                  <a
                    href={washingtonStateFormURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Form
                  </a>
                </div>
                {fileUploaded ? (
                  <button
                    className="oxbtn btn-grey"
                    style={{ marginLeft: "20px" }}
                  >
                    <i className="bi bi-check2-circle icon-large"></i>
                  </button>
                ) : (
                  <>
                    <button
                      className="oxbtn btn-grey"
                      onClick={() => handleFormUpload()}
                    >
                      Upload Form
                    </button>
                    <ToolTip
                      tooltipText="Please only upload .pdf type documents."
                      placement="top"
                      className="info-icon-spec"
                    />
                  </>
                )}
              </div>
            </div>

            <div style={{ flex: "0 0 100%" }}>
              {validationErrors.specialRelease && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.specialRelease}
                </div>
              )}
            </div> */}

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">First Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.firstName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={motorRelease?.firstName || ""}
                  onChange={(e) => handleInputChange(e, "firstName")}
                />
                {validationErrors.firstName && (
                  <div className="invalid-feedback">
                    {validationErrors.firstName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Middle Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={motorRelease?.middleName || ""}
                  onChange={(e) => handleInputChange(e, "middleName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Last Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.lastName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={motorRelease?.lastName || ""}
                  onChange={(e) => handleInputChange(e, "lastName")}
                />
                {validationErrors.lastName && (
                  <div className="invalid-feedback">
                    {validationErrors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Drivers License Number:</label>
              <input
                className={`form-control ${
                  validationErrors.licenseNumber ? "is-invalid" : ""
                }`}
                type="text"
                value={motorRelease.licenseNumber}
                onChange={(e) => handleInputChange(e, "licenseNumber")}
              />
              {validationErrors.licenseNumber && (
                <div className="invalid-feedback">
                  {validationErrors.licenseNumber}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Drivers License State:</label>
              <select
                className={`form-select ${
                  validationErrors.licenseState ? "is-invalid" : ""
                }`}
                value={motorRelease.licenseState}
                onChange={(e) => handleInputChange(e, "licenseState")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
              {validationErrors.licenseState && (
                <div className="invalid-feedback">
                  {validationErrors.licenseState}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Date of Birth:</label>
              <MonthDayYearPicker
                value={getSafePickerDate(motorRelease.dateOfBirth)}
                onChange={(newDate) =>
                  handleInputChange(
                    { target: { value: toSafeDateString(newDate) } } as any,
                    "dateOfBirth",
                  )
                }
              />
              {validationErrors.dateOfBirth && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.dateOfBirth}
                </div>
              )}
            </div>
          </div>

          <AuthorizationForm
            authText={
              "I attest that the answers I have given are true to the best of my knowledge and hereby consent to the release of the above-described records."
            }
            authorization={authorization}
            validationErrors={validationErrors}
            onInputChange={handleInputChange}
            type={""}
          />

          <div className="btn-center">
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};
export default MotorVehicleAuthorizationForm;
